import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'

export type LeavePolicySettingPayload = {
  annualLeave: number
  leaveAccrualRate: number
  sickLeave: number
  casualLeaves: number
  branchId: number
  mondayOff: boolean
  tuesdayOff: boolean
  wednesdayOff: boolean
  thursdayOff: boolean
  fridayOff: boolean
  saturdayOff: boolean
  sundayOff: boolean
}

type AnyRecord = Record<string, unknown>

export type LeavePolicySettingDto = {
  annualLeave: number
  leaveAccrualRate: number
  sickLeave: number
  casualLeaves: number
  branchId: number
  mondayOff: boolean
  tuesdayOff: boolean
  wednesdayOff: boolean
  thursdayOff: boolean
  fridayOff: boolean
  saturdayOff: boolean
  sundayOff: boolean
}

function boolFromRow(row: any, camel: string, pascal: string): boolean {
  const v = row?.[camel] ?? row?.[pascal]
  return v === true || v === 'true' || v === 1 || v === '1'
}

function normalizeLeavePolicy(row: any, fallbackBranchId: number): LeavePolicySettingDto {
  return {
    annualLeave: Number(row?.annualLeave ?? row?.AnnualLeave ?? row?.annualLeaveDays ?? row?.AnnualLeaveDays ?? 0),
    leaveAccrualRate: Number(row?.leaveAccrualRate ?? row?.LeaveAccrualRate ?? row?.accrualRate ?? row?.AccrualRate ?? 0),
    sickLeave: Number(row?.sickLeave ?? row?.SickLeave ?? 0),
    casualLeaves: Number(row?.casualLeaves ?? row?.CasualLeaves ?? 0),
    branchId: Number(row?.branchId ?? row?.BranchId ?? fallbackBranchId ?? 0),
    mondayOff: boolFromRow(row, 'mondayOff', 'MondayOff'),
    tuesdayOff: boolFromRow(row, 'tuesdayOff', 'TuesdayOff'),
    wednesdayOff: boolFromRow(row, 'wednesdayOff', 'WednesdayOff'),
    thursdayOff: boolFromRow(row, 'thursdayOff', 'ThursdayOff'),
    fridayOff: boolFromRow(row, 'fridayOff', 'FridayOff'),
    saturdayOff: boolFromRow(row, 'saturdayOff', 'SaturdayOff'),
    sundayOff: boolFromRow(row, 'sundayOff', 'SundayOff'),
  }
}

/**
 * Leave Policy service
 * Swagger: POST /api/LeavePolicySetting/SaveOrUpdate (application/json-patch+json)
 */
class LeavePolicyService {
  /** Some ERP endpoints return HTTP 200 with `{ result: { isError, msg } }`. */
  private assertResultOk(data: unknown, fallbackMsg: string): void {
    if (!data || typeof data !== 'object' || Array.isArray(data)) return
    const o = data as AnyRecord
    const r = (o.result ?? o.Result) as unknown
    if (!r || typeof r !== 'object' || Array.isArray(r)) return
    const ro = r as AnyRecord
    if (ro.isError === true || ro.IsError === true) {
      const msgRaw = ro.msg ?? ro.Msg ?? ro.message ?? ro.Message
      const msg =
        typeof msgRaw === 'string' && msgRaw.trim() ? msgRaw.trim() : fallbackMsg
      const err = new Error(msg) as Error & { response?: { data: unknown } }
      err.response = { data }
      throw err
    }
  }

  async saveOrUpdate(payload: LeavePolicySettingPayload): Promise<unknown> {
    const body: LeavePolicySettingPayload = {
      annualLeave: Number(payload.annualLeave || 0),
      leaveAccrualRate: Number(payload.leaveAccrualRate || 0),
      sickLeave: Number(payload.sickLeave || 0),
      casualLeaves: Number(payload.casualLeaves || 0),
      branchId: Number(payload.branchId || 0),
      mondayOff: Boolean(payload.mondayOff),
      tuesdayOff: Boolean(payload.tuesdayOff),
      wednesdayOff: Boolean(payload.wednesdayOff),
      thursdayOff: Boolean(payload.thursdayOff),
      fridayOff: Boolean(payload.fridayOff),
      saturdayOff: Boolean(payload.saturdayOff),
      sundayOff: Boolean(payload.sundayOff),
    }

    const response = await apiClient.post(endpoints.leavePolicySetting.saveOrUpdate, body, {
      headers: { 'Content-Type': 'application/json-patch+json' },
    })

    const data = response.data
    this.assertResultOk(data, 'Leave policy save/update failed.')
    return data
  }

  async getByBranchId(branchId: number): Promise<LeavePolicySettingDto | null> {
    const bId = Number(branchId || 0)
    const response = await apiClient.get(endpoints.leavePolicySetting.getByBranchId(bId))
    const payload = response.data

    const row =
      payload?.data ??
      payload?.Data ??
      payload?.result ??
      payload?.Result ??
      payload

    if (!row || typeof row !== 'object' || Array.isArray(row)) return null

    const dto = normalizeLeavePolicy(row, bId)
    const hasOffDay =
      dto.mondayOff ||
      dto.tuesdayOff ||
      dto.wednesdayOff ||
      dto.thursdayOff ||
      dto.fridayOff ||
      dto.saturdayOff ||
      dto.sundayOff

    const hasAnyValue =
      dto.annualLeave !== 0 ||
      dto.leaveAccrualRate !== 0 ||
      dto.sickLeave !== 0 ||
      dto.casualLeaves !== 0 ||
      hasOffDay

    return hasAnyValue ? dto : null
  }
}

export function isLeavePolicyOffDay(date: Date, policy: LeavePolicySettingDto | null | undefined): boolean {
  if (!policy) return false
  switch (date.getDay()) {
    case 0:
      return policy.sundayOff
    case 1:
      return policy.mondayOff
    case 2:
      return policy.tuesdayOff
    case 3:
      return policy.wednesdayOff
    case 4:
      return policy.thursdayOff
    case 5:
      return policy.fridayOff
    case 6:
      return policy.saturdayOff
    default:
      return false
  }
}

/** Working days in [from, to], excluding leave-policy off days (e.g. Saturday/Sunday). */
export function countWorkingLeaveDays(
  from: string,
  to: string,
  policy: LeavePolicySettingDto | null | undefined
): number {
  if (!from || !to) return 0
  const fromD = new Date(from + 'T00:00:00')
  const toD = new Date(to + 'T00:00:00')
  if (Number.isNaN(fromD.getTime()) || Number.isNaN(toD.getTime()) || toD.getTime() < fromD.getTime()) return 0
  let count = 0
  const d = new Date(fromD)
  while (d.getTime() <= toD.getTime()) {
    if (!isLeavePolicyOffDay(d, policy)) count++
    d.setDate(d.getDate() + 1)
  }
  return count
}

export function workingLeaveDaysOverlap(
  fromA: string,
  toA: string,
  fromB: string,
  toB: string,
  policy: LeavePolicySettingDto | null | undefined
): boolean {
  const aFrom = String(fromA || '').slice(0, 10)
  const aTo = String(toA || '').slice(0, 10)
  const bFrom = String(fromB || '').slice(0, 10)
  const bTo = String(toB || '').slice(0, 10)
  if (!aFrom || !aTo || !bFrom || !bTo) return false
  const from = aFrom > bFrom ? aFrom : bFrom
  const to = aTo < bTo ? aTo : bTo
  return countWorkingLeaveDays(from, to, policy) > 0
}

export function attendedWorkingDaysOverlap(
  from: string,
  to: string,
  attendedDates: string[] | undefined,
  policy: LeavePolicySettingDto | null | undefined
): boolean {
  const aFrom = String(from || '').slice(0, 10)
  const aTo = String(to || '').slice(0, 10)
  if (!aFrom || !aTo) return false
  return (attendedDates || []).some((raw) => {
    const d = String(raw || '').slice(0, 10)
    return d >= aFrom && d <= aTo && countWorkingLeaveDays(d, d, policy) > 0
  })
}

export const leavePolicyService = new LeavePolicyService()

